import { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase/config';

const LS_KEY = 'gormaran_clients_v1';
const API_URL = process.env.REACT_APP_API_URL || 'https://gormaran-growth-hub.onrender.com';

function sortClients(list) {
  return [...list].sort((a, b) =>
    (a.name || a.company || '').localeCompare(b.name || b.company || '', 'es', { sensitivity: 'base' }),
  );
}

function readLS() {
  try { return sortClients(JSON.parse(localStorage.getItem(LS_KEY) || '[]')); }
  catch { return []; }
}

async function fetchFromServer() {
  const user = auth.currentUser;
  if (!user) return null;
  const token = await user.getIdToken();
  const res = await fetch(`${API_URL}/api/clients`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  return res.json(); // { clients, deletedIds }
}

export function useClients() {
  const [clients, setClients] = useState(readLS);

  const refresh = useCallback(() => setClients(readLS()), []);

  useEffect(() => {
    // Hydrate from Firestore on mount — Firestore is primary source of truth
    fetchFromServer()
      .then(data => {
        if (!data || !Array.isArray(data.clients)) return;
        // Merge: Firestore wins, keep locally-created clients not yet synced,
        // and drop local copies of brands deleted elsewhere. Applied even when
        // the server list is empty, so the last deleted brand disappears too.
        const deleted   = new Set(data.deletedIds || []);
        const serverIds = new Set(data.clients.map(c => c.id));
        const localOnly = readLS().filter(c => !serverIds.has(c.id) && !deleted.has(c.id));
        const merged = sortClients([...data.clients, ...localOnly]);
        localStorage.setItem(LS_KEY, JSON.stringify(merged));
        setClients(merged);
        window.dispatchEvent(new Event('gormaran:clients'));
      })
      .catch(() => {
        // Network failure — fall back to localStorage (already set as initial state)
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener('storage', refresh);
    window.addEventListener('gormaran:clients', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('gormaran:clients', refresh);
    };
  }, [refresh]);

  return clients;
}
